/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react';
import FooterWC26 from '../../components/FooterWC26/FooterWC26';
import HeaderWC26 from '../../components/HeaderWC26/HeaderWC26';
import NameLink from '../../components/NameLink/NameLink';
import { MatchTooltip } from '../../components/ScoreboardWC26/ScoreTooltip';
import Spinner from '../../components/Spinner/Spinner';
import {
  useAllMatchStatsWC26,
  useBracketFixtures,
  useBracketStandings,
  useDirectFkGoals,
  useKeeperGoals,
  useOlimpicoGoals,
  useWC26ScoringRules,
  useWC26UserTeams,
} from '../../queries';
import {
  BracketFixture, BracketStanding, MatchStats,
  Scores,
  ScoringRules,
  UserTeams,
} from '../../types';
import { calculateResults, calculateScores, getMostRedsWinnerIds } from '../../utils/scoringUtils';
import KnockoutBracket from './KnockoutBracket';
import './WC26Bracket.css';

const ROUNDS: Record<string, string> = {
  'Group Stage - 1': 'Matchday 1',
  'Group Stage - 2': 'Matchday 2',
  'Group Stage - 3': 'Matchday 3',
};

const ZERO_TEAM_RESULTS = {
  wins: 0,
  draws: 0,
  redCards: 0,
  cleanSheets: 0,
  ownGoals: 0,
  teamGoals3plus: 0,
};

const DUMMY_TEAM_ID = '__none__';

function fixtureStatusLabel(status: string): string {
  if (status === 'FT' || status === 'AET' || status === 'PEN') return 'FT';
  if (['1H', '2H', 'ET', 'HT', 'LIVE'].includes(status)) return 'LIVE';
  return 'NS';
}

function compare3rdPlace(a: BracketStanding, b: BracketStanding): number {
  if (b.points !== a.points) return b.points - a.points;
  if (b.goalsDiff !== a.goalsDiff) return b.goalsDiff - a.goalsDiff;
  if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
  return a.team.name.localeCompare(b.team.name);
}

function getBest3rdPlaceIds(standings: BracketStanding[]): Set<number> {
  const thirdPlace = standings.filter(
    (s) => s.rank === 3 && s.group !== 'Group Stage'
  );
  const sorted = [...thirdPlace].sort(compare3rdPlace);
  return new Set(sorted.slice(0, 8).map((s) => s.team.id));
}

interface GroupData {
  standings: BracketStanding[];
  fixtures: BracketFixture[];
}

function buildGroups(
  standings: BracketStanding[],
  fixtures: BracketFixture[]
): Record<string, GroupData> {
  const teamToGroup: Record<number, string> = {};
  standings.forEach((s) => {
    if (s.group === 'Group Stage') return;
    teamToGroup[s.team.id] = s.group;
  });

  const groups: Record<string, GroupData> = {};
  standings.forEach((s) => {
    if (!groups[s.group]) groups[s.group] = { standings: [], fixtures: [] };
    groups[s.group].standings.push(s);
  });

  fixtures.forEach((f) => {
    if (!Object.keys(ROUNDS).includes(f.round)) return;
    const group = teamToGroup[f.home.id];
    if (group && groups[group]) groups[group].fixtures.push(f);
  });

  return groups;
}

interface GroupCardProps {
  groupName: string;
  data: GroupData;
  qualifying3rdIds: Set<number>;
  userTeams: UserTeams[];
  matchStatsById: Record<number, MatchStats>;
  scoringRules?: ScoringRules;
  directFkGoals: string[];
  olimpicoGoals: string[];
  keeperGoals: string[];
  isThirdPlaceTable?: boolean;
}

function GroupCard({
  groupName, data, qualifying3rdIds, userTeams, matchStatsById, scoringRules,
  directFkGoals, olimpicoGoals, keeperGoals, isThirdPlaceTable,
}: GroupCardProps) {
  const findPlayer = (teamId: number) => {
    const id = teamId.toString();
    const found = userTeams.find((u) => u.team1Id === id || u.team2Id === id);
    return found?.name ?? '';
  };

  const allMatchStatsList = Object.values(matchStatsById);
  const allTeamResults = calculateResults(userTeams, allMatchStatsList);
  const mostRedsWinnerIds = getMostRedsWinnerIds(allTeamResults, userTeams);

  const findScore = (fixtureId: number, teamId: number): Scores | null => {
    const teamIdStr = teamId.toString();
    const userTeam = userTeams.find(
      (u) => u.team1Id === teamIdStr || u.team2Id === teamIdStr
    );
    const match = matchStatsById[fixtureId];
    if (!userTeam || !match || !scoringRules) return null;

    const teamResults = calculateResults([userTeam], [match]);
    const teamResultsWithDummy = { ...teamResults, [DUMMY_TEAM_ID]: { ...ZERO_TEAM_RESULTS } };
    const isT1 = teamIdStr === userTeam.team1Id;
    const soloUser = {
      ...userTeam,
      team1Id: isT1 ? teamIdStr : DUMMY_TEAM_ID,
      team2Id: isT1 ? DUMMY_TEAM_ID : teamIdStr,
    };
    const [score] = calculateScores(
      teamResultsWithDummy,
      [soloUser],
      scoringRules,
      undefined,
      undefined,
      undefined,
      undefined,
      [match],
      directFkGoals,
      olimpicoGoals,
      keeperGoals,
      mostRedsWinnerIds
    );
    return score;
  };
  const groupWinBonus = (teamId: number): number | null => {
    if (!scoringRules) return null;
    const idStr = teamId.toString();
    const userTeam = userTeams.find((u) => u.team1Id === idStr || u.team2Id === idStr);
    if (!userTeam) return null;
    return idStr === userTeam.team1Id ? scoringRules.t1WinGroup : scoringRules.t2WinGroup;
  };

  const totalFor = (score: Scores) => score.winsScore + score.drawsScore
    + score.cleanSheetsScore + score.redCardsScore + score.ownGoalsScore
    + score.hattrick + score.directFk + score.olimpico + score.keeperGoal
    + score.mostReds + score.teamGoals3plus;

  const sortedStandings = isThirdPlaceTable
    ? [...data.standings].sort(compare3rdPlace)
    : [...data.standings].sort((a, b) => a.rank - b.rank);
  const sortedFixtures = [...data.fixtures].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const rounds = Array.from(new Set(sortedFixtures.map((f) => f.round)));

  return (
    <div className="bracket-group">
      <div className="bracket-group-header">{groupName}</div>

      <table className="bracket-standings">
        <thead>
          <tr>
            <th>Team</th>
            <th>P</th>
            <th>W</th>
            <th>D</th>
            <th>L</th>
            <th>GD</th>
            <th>Pts</th>
          </tr>
        </thead>
        <tbody>
          {sortedStandings.map((s, i) => {
            const top2 = !isThirdPlaceTable && s.rank <= 2 && s.all.played >= 3;
            const best3rd = isThirdPlaceTable
              ? qualifying3rdIds.has(s.team.id)
              : !top2 && s.rank === 3 && qualifying3rdIds.has(s.team.id);
            const rowClass = top2
              ? 'bracket-standing-qualified'
              : best3rd ? 'bracket-standing-best3rd' : '';

            const bonus = !isThirdPlaceTable && s.rank === 1 && findPlayer(s.team.id)
              ? groupWinBonus(s.team.id) : null;

            return (
              <tr key={s.team.id} className={rowClass}>
                <td>
                  <div className="bracket-standing-team">
                    {top2 && <span className="bracket-qualified-dot" />}
                    {best3rd && <span className="bracket-best3rd-dot" />}
                    {!top2 && !best3rd && (
                      <span className="bracket-standing-rank">{isThirdPlaceTable ? i + 1 : s.rank}</span>
                    )}
                    <img src={s.team.logo} alt={s.team.name} />
                    <div className="bracket-fixture-team-info">
                      <span>{s.team.name}</span>
                      {findPlayer(s.team.id) && (
                        <span className="bracket-fixture-player">
                          <NameLink name={findPlayer(s.team.id)} />
                          {bonus !== null && bonus !== 0 && (
                            <span className="bracket-fixture-points positive">
                              {`+${bonus}`}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td>{s.all.played}</td>
                <td>{s.all.win}</td>
                <td>{s.all.draw}</td>
                <td>{s.all.lose}</td>
                <td>{s.goalsDiff > 0 ? `+${s.goalsDiff}` : s.goalsDiff}</td>
                <td><strong>{s.points}</strong></td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="bracket-fixtures-divider" />

      {rounds.map((round) => (
        <div key={round}>
          <div className="bracket-round-label">{ROUNDS[round] ?? round}</div>
          {sortedFixtures
            .filter((f) => f.round === round)
            .map((f) => {
              const liveMatch = matchStatsById[f.id];
              const statusKey = fixtureStatusLabel(liveMatch?.status?.short ?? f.status.short);
              const homeGoals = liveMatch?.score.home ?? f.goals.home;
              const awayGoals = liveMatch?.score.away ?? f.goals.away;
              const kickoff = new Date(f.date).toLocaleString([], {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
              });
              const homeScore = statusKey !== 'NS' ? findScore(f.id, f.home.id) : null;
              const awayScore = statusKey !== 'NS' ? findScore(f.id, f.away.id) : null;
              const homePoints = homeScore ? totalFor(homeScore) : null;
              const awayPoints = awayScore ? totalFor(awayScore) : null;
              return (
                <div key={f.id} className="bracket-fixture">
                  <div className="bracket-fixture-team">
                    <img src={f.home.logo} alt={f.home.name} />
                    <div className="bracket-fixture-team-info">
                      <span>{f.home.name}</span>
                      {findPlayer(f.home.id) && (
                        <span className="bracket-fixture-player">
                          <NameLink name={findPlayer(f.home.id)} />
                          {homePoints !== null && homeScore && (
                            <span className={`bracket-fixture-points ${homePoints > 0 ? 'positive' : homePoints < 0 ? 'negative' : 'zero'}`}>
                              <MatchTooltip
                                score={homeScore}
                                label={homePoints >= 0 ? `+${homePoints}` : `${homePoints}`}
                              />
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="bracket-fixture-score">
                    {statusKey === 'NS' ? (
                      <>
                        <span className="bracket-fixture-vs">vs</span>
                        <span className="bracket-fixture-time">{kickoff}</span>
                      </>
                    ) : (
                      <>
                        <div className="bracket-fixture-scoreline">
                          <span>{homeGoals ?? '-'}</span>
                          <span className="bracket-fixture-vs">–</span>
                          <span>{awayGoals ?? '-'}</span>
                        </div>
                        <span className={`bracket-fixture-status ${statusKey.toLowerCase()}`}>
                          {statusKey === 'LIVE' && f.status.elapsed
                            ? `${f.status.elapsed}'`
                            : statusKey}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="bracket-fixture-team away">
                    <img src={f.away.logo} alt={f.away.name} />
                    <div className="bracket-fixture-team-info">
                      <span>{f.away.name}</span>
                      {findPlayer(f.away.id) && (
                        <span className="bracket-fixture-player">
                          <NameLink name={findPlayer(f.away.id)} />
                          {awayPoints !== null && awayScore && (
                            <span className={`bracket-fixture-points ${awayPoints > 0 ? 'positive' : awayPoints < 0 ? 'negative' : 'zero'}`}>
                              <MatchTooltip
                                score={awayScore}
                                label={awayPoints >= 0 ? `+${awayPoints}` : `${awayPoints}`}
                              />
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      ))}
    </div>
  );
}

function WC26Bracket() {
  const [fixturesLoading, fixtures, fixturesError] = useBracketFixtures();
  const [standingsLoading, standings, standingsError] = useBracketStandings();
  const [, userTeams] = useWC26UserTeams();
  const [, allMatchStats] = useAllMatchStatsWC26();
  const [, scoringRules] = useWC26ScoringRules();
  const [, directFkGoals] = useDirectFkGoals();
  const [, olimpicoGoals] = useOlimpicoGoals();
  const [, keeperGoals] = useKeeperGoals();
  const [tab, setTab] = useState<'groups' | 'knockout'>('knockout');

  useEffect(() => {
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon) favicon.href = '/favicon_wc.ico';
  }, []);

  const loading = fixturesLoading || standingsLoading;
  const error = fixturesError || standingsError;

  const groups = (!loading && fixtures && standings)
    ? buildGroups(standings, fixtures)
    : null;

  const qualifying3rdIds = standings ? getBest3rdPlaceIds(standings) : new Set<number>();

  const matchStatsById = (allMatchStats ?? []).reduce<Record<number, MatchStats>>((acc, m) => {
    acc[m.id] = m;
    return acc;
  }, {});

  return (
    <div className="bracket-page">
      <HeaderWC26 />
      <div className="bracket-main">
        {error && <p className="bracket-empty">Failed to load bracket data.</p>}
        {loading && <Spinner />}
        {!loading && !error && (
          <>
            <div className="bracket-controls">
              <div className="bracket-tabs">
                <button
                  type="button"
                  className={`bracket-tab ${tab === 'groups' ? 'active' : ''}`}
                  onClick={() => setTab('groups')}
                >
                  Group Stage
                </button>
                <button
                  type="button"
                  className={`bracket-tab ${tab === 'knockout' ? 'active' : ''}`}
                  onClick={() => setTab('knockout')}
                >
                  Knockout
                </button>
              </div>
            </div>
            {tab === 'groups' && (
              <div className="bracket-legend">
                <span>
                  <span className="bracket-qualified-dot" />
                  {' '}
                  Qualified (Top 2)
                </span>
                <span>
                  <span className="bracket-best3rd-dot" />
                  {' '}
                  Currently qualifying (Best 3rd)
                </span>
              </div>
            )}

            {tab === 'groups' && groups && (
              <div className="bracket-groups-grid">
                {Object.entries(groups)
                  .sort(([a], [b]) => {
                    if (a === 'Group Stage') return 1;
                    if (b === 'Group Stage') return -1;
                    return a.localeCompare(b);
                  })
                  .map(([groupName, data]) => (
                    <GroupCard
                      key={groupName}
                      groupName={groupName === 'Group Stage' ? 'Best Third-Placed Teams' : groupName}
                      isThirdPlaceTable={groupName === 'Group Stage'}
                      data={data}
                      qualifying3rdIds={qualifying3rdIds}
                      userTeams={userTeams ?? []}
                      matchStatsById={matchStatsById}
                      scoringRules={scoringRules}
                      directFkGoals={directFkGoals ?? []}
                      olimpicoGoals={olimpicoGoals ?? []}
                      keeperGoals={keeperGoals ?? []}
                    />
                  ))}
              </div>
            )}

            {tab === 'knockout' && standings && (
              <KnockoutBracket
                standings={standings}
                qualifying3rdIds={qualifying3rdIds}
                userTeams={userTeams ?? []}
                bracketFixtures={fixtures ?? []}
                matchStatsById={matchStatsById}
                scoringRules={scoringRules}
                directFkGoals={directFkGoals ?? []}
                olimpicoGoals={olimpicoGoals ?? []}
                keeperGoals={keeperGoals ?? []}
              />
            )}
          </>
        )}
      </div>
      <FooterWC26 />
    </div>
  );
}

export default WC26Bracket;
